import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../lib/firebase';
import { FirebaseError } from 'firebase/app';
import { cn } from '../lib/utils';
import KodaCareLogoFull from '../components/Custom Icons/KodaCareLogoFull';
import { Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../functions/useLanguage';
import AppButton from '../components/AppButton';
import { resolvePostAuthDestination } from '../lib/pendingInvite';

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continueUrl');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const firebaseErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':    return t('login.error.incorrectCredentials');
      case 'auth/email-not-verified':    return t('login.error.notVerified');
      case 'auth/too-many-requests':     return t('login.error.tooManyRequests');
      case 'auth/network-request-failed': return t('login.error.network');
      default:                           return t('login.error.generic');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password, rememberMe);
      if (!auth.currentUser?.emailVerified) {
        navigate(continueUrl ? `/verify-email?continueUrl=${encodeURIComponent(continueUrl)}` : '/verify-email');
      } else {
        navigate(resolvePostAuthDestination(continueUrl));
      }
    } catch (err) {
      if (err instanceof FirebaseError) {
        setError(firebaseErrorMessage(err.code));
      } else {
        setError(t('login.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setIsLoading(true);
    try {
      await loginWithGoogle();
      navigate(resolvePostAuthDestination(continueUrl));
    } catch (err) {
      if (err instanceof FirebaseError) {
        setError(firebaseErrorMessage(err.code));
      } else {
        setError(t('login.error.googleFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("min-h-screen flex flex-col items-center justify-center p-4", "bg-background-light")}>
      <div className={cn("w-full max-w-md overflow-hidden", "bg-background-light")}>
        {/* Header */}
        <div className={cn("p-8 text-center", "bg-background-light")}>
          <div className="w-full h-auto flex items-center justify-center mx-auto mb-0">
            <KodaCareLogoFull className="w-full h-auto text-primary-brown" />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <h2 className={cn("text-xl font-bold mb-6", "text-primary-brown")}>{t('login.signIn')}</h2>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">

              <div>
                <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('login.email')}</label>
                <input 
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn("w-full px-3 py-2.5 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40")}
                  placeholder="name@example.com"
                />
              </div>

              <div>
                <label className={cn("block text-sm font-medium mb-1", "text-text-secondary")}>{t('login.password')}</label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={cn("w-full px-3 py-2.5 pr-11 rounded-xl border border-slate-200 text-text-primary font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40")}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className={cn("absolute right-3 top-1/2 -translate-y-1/2", "text-gray-400 hover:text-text-secondary")}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="flex justify-end mt-1">
                  <Link
                    to="/forgot-password"
                    className={cn("text-xs font-medium hover:underline", "text-primary-brown")}
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </div>

              {/* Remember me */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded accent-primary-brown cursor-pointer"
                />
                <span className="text-sm text-text-secondary">{t('login.rememberMe')}</span>
              </label>

              <AppButton
                type="submit"
                disabled={isLoading}
                className="p-4"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('login.signIn')
                )}
              </AppButton>

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className={cn("flex-1 h-px", "bg-gray-200")} />
                <span className={cn("text-xs font-medium", "text-gray-400")}>{t('login.or')}</span>
                <div className={cn("flex-1 h-px", "bg-gray-200")} />
              </div>

              {/* Google Sign-in */}
              <AppButton
                type="button"
                variant="secondary"
                onClick={handleGoogle}
                disabled={isLoading}
                className="p-3 mt-4 font-medium gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                {t('login.continueWithGoogle')}
              </AppButton>

              {/* Register link */}
              <p className={cn("text-center text-sm mt-2", "text-text-secondary")}>
                {t('login.noAccount')}{' '}
                <Link to={continueUrl ? `/register?continueUrl=${encodeURIComponent(continueUrl)}` : "/register"} className="font-semibold text-primary-brown hover:underline">
                  {t('login.register')}
                </Link>
              </p>

          </form>
        </div>
      </div>
    </div>
  );
}
